import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastProvider } from './utils/Toast';
import { ThemeProvider } from './ThemeContext';
import { AuthProvider, useAuth } from './AuthContext';
import Login from './Login';
import ResetPassword from './ResetPassword';
import CreateAccount from './CreateAccount';
import SiteDashboard from './SiteDashboard';
import AdminDashboard from './AdminDashboard';
import ClientDashboard from './ClientDashboard';
import ManagerDashboard from './ManagerDashboard';
import SiteCreation from './SiteCreation';
import SiteDetail from './SiteDetail';
import NotAuthorized from './NotAuthorized';
import type { UserRole } from './types/auth';
import 'semantic-ui-css/semantic.min.css';

const resolveLandingRoute = (role: UserRole | null | undefined): string => {
  switch (role) {
    case 'client':
      return '/user-dashboard';
    case 'admin':
      return '/dashboard';
    case 'admin_client':
      return '/manager-dashboard';
    default:
      return '/dashboard';
  }
};

const ProtectedRoute: React.FC<{ children: React.ReactElement; allowedRoles?: UserRole[] }> = ({ children, allowedRoles }) => {
  const { user, loading, role } = useAuth();
  const location = useLocation();

  if (loading || (allowedRoles && allowedRoles.length > 0 && role == null)) {
    return null;
  }

  if (!user) {
    return <Navigate to="/" replace state={{ from: location.pathname }} />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const isAllowed = role ? allowedRoles.includes(role) : false;
    if (!isAllowed) {
      const fallbackRoute = resolveLandingRoute(role);
      if (fallbackRoute === location.pathname) {
        return <Navigate to="/no-access" replace state={{ from: location.pathname }} />;
      }
      return <Navigate to={fallbackRoute} replace state={{ from: location.pathname }} />;
    }
  }

  return children;
};

const PublicRoute: React.FC<{ children: React.ReactElement }> = ({ children }) => {
  const { user, loading, role } = useAuth();

  if (loading || (user && role == null)) {
    return null;
  }

  if (user) {
    const fallbackRoute = resolveLandingRoute(role);
    return <Navigate to={fallbackRoute} replace />;
  }

  return children;
};

const App: React.FC = () => {
  return (
    <ThemeProvider>
      <ToastProvider>
        <AuthProvider>
          <Router>
            <Routes>
              <Route
                path="/"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/create-account"
                element={
                  <PublicRoute>
                    <CreateAccount />
                  </PublicRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/site-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <SiteDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/user-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["client"]}>
                    <ClientDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/manager-dashboard"
                element={
                  <ProtectedRoute allowedRoles={["admin_client"]}>
                    <ManagerDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sites/:id"
                element={
                  <ProtectedRoute>
                    <SiteDetail />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sites/create"
                element={
                  <ProtectedRoute allowedRoles={["admin", "admin_client"]}>
                    <SiteCreation />
                  </ProtectedRoute>
                }
              />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/no-access" element={<NotAuthorized />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>
      </ToastProvider>
    </ThemeProvider>
  );
};

export default App;
